import React from 'react';
import { Grid, Typography, TextField, FormHelperText } from '@material-ui/core';
import {
  Field,
  FormikValues,
  useFormikContext,
  getIn,
  FieldProps,
  ErrorMessage,
} from 'formik';

import TextInputWithFormat from 'components/TextInputWithFormat';
import { IRangeFieldProps } from './interfaces';

const RangeField: React.FC<IRangeFieldProps> = ({
  name,
  labelBefore = 'de',
  labelBetween = 'até',
  labelAfter = 'dias',
  isMoney = false,
  disabled = false,
}: IRangeFieldProps) => {
  const { errors, touched } = useFormikContext<FormikValues>();

  const rangeError = getIn(errors, name);
  const isFieldErrored = typeof rangeError === 'string';

  const bothTouched =
    getIn(touched, `${name}.min`) && getIn(touched, `${name}.max`);
  const minError =
    getIn(touched, `${name}.min`) && getIn(errors, `${name}.min`);
  const maxError =
    getIn(touched, `${name}.max`) && getIn(errors, `${name}.max`);

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="stretch"
    >
      {isFieldErrored && bothTouched && (
        <Grid data-cy="range-field__error--range-field-error" item xs={12}>
          <FormHelperText error>
            <ErrorMessage name={name} />
          </FormHelperText>
        </Grid>
      )}
      {bothTouched && (
        <React.Fragment>
          <Grid data-cy="range-field__error--range-min-error" item xs={12}>
            <FormHelperText error>
              <ErrorMessage name={`${name}.min`} />
            </FormHelperText>
          </Grid>
          <Grid data-cy="range-field__error--range-max-error" item xs={12}>
            <FormHelperText error>
              <ErrorMessage name={`${name}.max`} />
            </FormHelperText>
          </Grid>
        </React.Fragment>
      )}
      <Grid
        item
        xs={12}
        container
        direction="row"
        justifyContent="space-around"
        alignItems="center"
      >
        {labelBefore && (
          <Grid item xs={labelBefore.length > 12 ? 2 : 1}>
            <Typography>{labelBefore}</Typography>
          </Grid>
        )}
        <Grid data-cy="range-field__input--min-value" item xs={4}>
          {isMoney ? (
            <Field
              type="number"
              name={`${name}.min`}
              component={TextInputWithFormat}
              prefix="R$"
              error={bothTouched && (isFieldErrored || !!minError)}
              showErrorText={false}
              disabled={disabled}
            />
          ) : (
            <Field name={`${name}.min`}>
              {({ field }: FieldProps) => (
                <TextField
                  type="number"
                  error={bothTouched && isFieldErrored}
                  helperText=""
                  disabled={disabled}
                  {...field}
                />
              )}
            </Field>
          )}
        </Grid>
        {labelBetween && (
          <Grid item xs={labelBetween.length > 12 ? 2 : 1}>
            <Typography align="center">{labelBetween}</Typography>
          </Grid>
        )}
        <Grid data-cy="range-field__input--max-value" item xs={4}>
          {isMoney ? (
            <Field
              type="number"
              name={`${name}.max`}
              component={TextInputWithFormat}
              prefix="R$"
              error={bothTouched && (isFieldErrored || !!maxError)}
              showErrorText={false}
              disabled={disabled}
            />
          ) : (
            <Field name={`${name}.max`}>
              {({ field }: FieldProps) => (
                <TextField
                  type="number"
                  error={bothTouched && (isFieldErrored || !!maxError)}
                  helperText=""
                  disabled={disabled}
                  {...field}
                />
              )}
            </Field>
          )}
        </Grid>
        <Grid item xs={1} container direction="row" justifyContent="flex-end">
          {labelAfter && !isMoney && (
            <Typography align="left">{labelAfter}</Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RangeField;
